import { Controller } from "@hotwired/stimulus"
import iconRemove from "../../images/icon-mypage-modal-close.svg"

export default class extends Controller {
  static targets = [
    "modal", "overlay",
    "checkbox", "sectionCheckbox",
    "item", "section",
    "tab", "panel",
    "categorySearch",
    "confirmButton",
    "hiddenInputs", "input", "tagContainer", "validationInput",
    "backCategoryName"
  ]
  static values = { hiddenInputName: String }

  connect() {
    this.selectedIds = []
    this.selectedNames = {}

    if (this.hasHiddenInputsTarget) {
      this.hiddenInputsTarget.querySelectorAll('input[type="hidden"]').forEach(input => {
        if (input.value) this.selectedIds.push(input.value)
      })
    }

    this.checkboxTargets.forEach(cb => {
      if (this.selectedIds.includes(cb.value) && cb.dataset.name) {
        this.selectedNames[cb.value] = cb.dataset.name
      }
    })

    this._renderTags()

    if (this.hasValidationInputTarget && this.validationInputTarget.value.trim() !== "") {
      setTimeout(() => {
        this.validationInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
      }, 0)
    }
  }

  openModal() {
    this.modalTarget.classList.add("is-open")
    this.overlayTarget.classList.add("is-open")
    document.body.style.overflow = "hidden"

    if (this.hasCategorySearchTarget) {
      this.categorySearchTargets.forEach(input => { input.value = "" })
    }
    this._showAllItems()

    this.checkboxTargets.forEach(cb => {
      cb.checked = this._isSingleSelect
        ? this.selectedIds[0] === cb.value
        : this.selectedIds.includes(cb.value)
    })

    this._syncSectionCheckboxes()
  }

  closeModal() {
    this.modalTarget.classList.remove("is-open")
    this.modalTarget.classList.remove("is-sp-skills-view")
    this.modalTarget.classList.remove("is-sp-search-view")
    this.overlayTarget.classList.remove("is-open")
    document.body.style.overflow = ""
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.closeModal()
    }
  }

  switchTab(event) {
    const category = event.currentTarget.dataset.category
    this.tabTargets.forEach(tab => {
      tab.classList.toggle("is-active", tab.dataset.category === category)
    })
    this.panelTargets.forEach(panel => {
      panel.style.display = ""
      panel.classList.toggle("is-active", panel.dataset.category === category)
    })
    this.categorySearchTargets.forEach(input => { input.value = "" })
    this._showAllItems()
  }

  showSpCategory(event) {
    const category = event.currentTarget.dataset.category
    this.panelTargets.forEach(panel => {
      panel.style.display = ""
      panel.classList.toggle("is-active", panel.dataset.category === category)
    })
    this.categorySearchTargets.forEach(input => { input.value = "" })
    this._showAllItems()
    if (this.hasBackCategoryNameTarget) {
      this.backCategoryNameTarget.textContent = category
    }
    this.modalTarget.classList.add("is-sp-skills-view")
  }

  goBack() {
    this.modalTarget.classList.remove("is-sp-skills-view")
    this.categorySearchTargets.forEach(input => { input.value = "" })
    this._showAllItems()
  }

  search(event) {
    const query = event.currentTarget.value.trim().toLowerCase()

    if (query === "") {
      this._showAllItems()
      return
    }

    if (this.hasTabTarget) {
      // SPでは検索結果ビューに切り替え
      this.modalTarget.classList.add("is-sp-search-view")

      this.sectionTargets.forEach(section => {
        const middleCategory = (section.dataset.middleCategory || "").toLowerCase()
        const panel = section.closest("[data-large-category]")
        const largeCategory = panel ? (panel.dataset.largeCategory || "").toLowerCase() : ""

        if (largeCategory.includes(query) || middleCategory.includes(query)) {
          section.style.display = ""
          section.querySelectorAll("[data-mypage-checkbox-modal-target='item']").forEach(item => {
            item.style.display = ""
          })
          return
        }

        const items = section.querySelectorAll("[data-mypage-checkbox-modal-target='item']")
        let hasVisible = false
        items.forEach(item => {
          const name = (item.dataset.itemName || "").toLowerCase()
          const visible = name.includes(query)
          item.style.display = visible ? "" : "none"
          if (visible) hasVisible = true
        })
        section.style.display = hasVisible ? "" : "none"
      })

      const visiblePanels = []
      this.panelTargets.forEach(panel => {
        const sections = panel.querySelectorAll("[data-mypage-checkbox-modal-target='section']")
        const hasVisible = Array.from(sections).some(s => s.style.display !== "none")
        panel.style.display = hasVisible ? "block" : "none"
        if (hasVisible) visiblePanels.push(panel)
      })

      const singleMatch = visiblePanels.length === 1
      this.tabTargets.forEach(tab => {
        const isActive = singleMatch && visiblePanels[0].dataset.category === tab.dataset.category
        tab.classList.toggle("is-active", isActive)
      })
    } else {
      const sections = this.element.querySelectorAll(".mypage-profile-section__category-modal-section")
      if (sections.length > 0) {
        sections.forEach(section => {
          const sectionName = (section.dataset.sectionName || "").toLowerCase()
          if (sectionName.includes(query)) {
            section.style.display = ""
            section.querySelectorAll(".mypage-profile-section__category-modal-item").forEach(item => {
              item.style.display = ""
            })
            return
          }
          const items = section.querySelectorAll(".mypage-profile-section__category-modal-item")
          let hasVisible = false
          items.forEach(item => {
            const cb = item.querySelector("input")
            const name = (cb ? cb.dataset.name || "" : "").toLowerCase()
            const visible = name.includes(query)
            item.style.display = visible ? "" : "none"
            if (visible) hasVisible = true
          })
          section.style.display = hasVisible ? "" : "none"
        })
      } else {
        this.element.querySelectorAll(".mypage-profile-section__category-modal-item").forEach(item => {
          const cb = item.querySelector("input")
          const name = (cb ? cb.dataset.name || "" : "").toLowerCase()
          item.style.display = name.includes(query) ? "" : "none"
        })
      }
    }
  }

  clearSearch() {
    this.categorySearchTargets.forEach(input => { input.value = "" })
    this._showAllItems()
  }

  toggleSection(event) {
    const sectionCb = event.currentTarget
    const sectionKey = sectionCb.dataset.section || sectionCb.dataset.sectionId
    const isChecked = sectionCb.checked
    this.checkboxTargets.forEach(cb => {
      if (cb.dataset.section !== sectionKey) return
      const item = cb.closest("[data-mypage-checkbox-modal-target='item']") ||
                   cb.closest(".mypage-profile-section__category-modal-item")
      if (item && item.style.display === "none") return
      cb.checked = isChecked
    })
  }

  apply() {
    if (this._isSingleSelect) {
      const checked = this.checkboxTargets.find(cb => cb.checked)
      if (checked) {
        this.selectedIds = [checked.value]
        this.selectedNames = { [checked.value]: checked.dataset.name || "" }
      } else {
        this.selectedIds = []
        this.selectedNames = {}
      }
    } else {
      this.selectedIds = []
      this.selectedNames = {}
      this.checkboxTargets.forEach(cb => {
        if (cb.checked) {
          this.selectedIds.push(cb.value)
          this.selectedNames[cb.value] = cb.dataset.name || ""
        }
      })
    }

    this._updateInputTarget()
    this._renderHiddenInputs()
    if (this.hasValidationInputTarget) {
      this.validationInputTarget.value = this.selectedIds.join(",")
      this.validationInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this._renderTags()
    this.closeModal()
  }

  confirmSelection() {
    this.apply()
  }

  clearAll() {
    this.categorySearchTargets.forEach(input => { input.value = "" })
    this._showAllItems()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    this.sectionCheckboxTargets.forEach(cb => { cb.checked = false; cb.indeterminate = false })
    this.selectedIds = []
    this.selectedNames = {}
    this._updateInputTarget()
    this._renderHiddenInputs()
    if (this.hasValidationInputTarget) {
      this.validationInputTarget.value = ""
      this.validationInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this._renderTags()
  }

  removeTag(event) {
    const tag = event.currentTarget.closest(".mypage-profile-section__category-tag")
    if (!tag) return
    const id = tag.dataset.tagId
    if (id) {
      this.selectedIds = this.selectedIds.filter(sid => sid !== id)
      delete this.selectedNames[id]
      this.checkboxTargets.forEach(cb => {
        if (cb.value === id) cb.checked = false
      })
    }
    tag.remove()
    this._updateInputTarget()
    this._renderHiddenInputs()
    if (this.hasValidationInputTarget) {
      this.validationInputTarget.value = this.selectedIds.join(",")
      this.validationInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  get _isSingleSelect() {
    return this.checkboxTargets.length > 0 && this.checkboxTargets[0].type === "radio"
  }

  _updateInputTarget() {
    if (!this.hasInputTarget) return
    const names = this.selectedIds.map(id => this.selectedNames[id] || "").filter(Boolean)
    this.inputTarget.value = names.join(", ")
  }

  _renderHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return
    const name = this._hiddenInputName
    if (!name) return
    if (this._isSingleSelect) {
      this.hiddenInputsTarget.innerHTML = this.selectedIds[0]
        ? `<input type="hidden" name="${name}" value="${this.selectedIds[0]}">`
        : ""
    } else {
      this.hiddenInputsTarget.innerHTML = this.selectedIds
        .map(id => `<input type="hidden" name="${name}" value="${id}">`)
        .join("")
    }
  }

  get _hiddenInputName() {
    if (this.hasHiddenInputNameValue && this.hiddenInputNameValue !== "") {
      return this.hiddenInputNameValue
    }
    if (!this.hasInputTarget) return ""
    const map = {
      "preferred-career-industry-names": "member_profile[member_preferred_career_industries_attributes][][career_industry_id]",
      "preferred-job-category-names":    "member_profile[member_preferred_job_categories_attributes][][job_category_id]",
      "preferred-location-names":        "member_profile[member_preferred_locations_attributes][][location_id]",
      "preferred-employment-kind-names": "member_profile[member_preferred_employment_kinds_attributes][][employment_kind_id]",
      "career-industry-names":           "member_profile[member_career_industry_attributes][career_industry_id]",
      "job-category-names":              "member_profile[member_job_category_attributes][job_category_id]",
    }
    return map[this.inputTarget.id] || ""
  }

  _syncSectionCheckboxes() {
    if (!this.hasSectionCheckboxTarget) return
    this.sectionCheckboxTargets.forEach(sectionCb => {
      const sectionKey = sectionCb.dataset.section || sectionCb.dataset.sectionId
      const jobCbs = this.checkboxTargets.filter(cb => cb.dataset.section === sectionKey)
      if (jobCbs.length === 0) return
      const checkedCount = jobCbs.filter(cb => cb.checked).length
      sectionCb.checked = checkedCount === jobCbs.length
      sectionCb.indeterminate = checkedCount > 0 && checkedCount < jobCbs.length
    })
  }

  _showAllItems() {
    this.itemTargets.forEach(item => { item.style.display = "" })
    this.sectionTargets.forEach(section => { section.style.display = "" })
    this.panelTargets.forEach(panel => { panel.style.display = "" })
    this.panelTargets.forEach(panel => {
      const category = panel.dataset.category
      const tab = this.tabTargets.find(t => t.dataset.category === category)
      if (tab) tab.classList.toggle("is-active", panel.classList.contains("is-active"))
    })
    this.element.querySelectorAll(".mypage-profile-section__category-modal-item").forEach(item => {
      item.style.display = ""
    })
    this.element.querySelectorAll(".mypage-profile-section__category-modal-section").forEach(section => {
      section.style.display = ""
    })
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("is-sp-search-view")
    }
  }

  _renderTags() {
    if (!this.hasTagContainerTarget) return
    const container = this.tagContainerTarget
    container.innerHTML = ""
    this.selectedIds.forEach(id => {
      const name = this.selectedNames[id]
      if (!name) return
      const tag = document.createElement("div")
      tag.className = "mypage-profile-section__category-tag"
      tag.dataset.tagId = id
      tag.innerHTML = `
        <span class="mypage-profile-section__category-tag-name">${name}</span>
        <button type="button"
                class="mypage-profile-section__category-tag-remove"
                data-action="click->mypage-checkbox-modal#removeTag"
                data-tag-id="${id}"
                aria-label="削除">
          <img src="${iconRemove}" alt="削除" width="10" height="10">
        </button>
      `
      container.appendChild(tag)
    })
  }
}
